import React, { useMemo } from 'react';
import { Modal, View, ScrollView, Image, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { SavedRecommendation } from '@/services/recommendations';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';

type SavedRecommendationDetailModalProps = {
  visible: boolean;
  recommendation: SavedRecommendation | null;
  onClose: () => void;
};

export function SavedRecommendationDetailModal({
  visible,
  recommendation,
  onClose,
}: SavedRecommendationDetailModalProps) {
  const theme = useTheme();

  const destination = recommendation?.destination || '';
  const titleText = useMemo(() => {
    if (!recommendation) return '';
    const t = typeof recommendation.title === 'string' ? recommendation.title.trim() : '';
    if (t) return t;
    return destination;
  }, [recommendation, destination]);

  const { imageUrl } = useLandmarkImage(destination, recommendation?.coverImage ?? undefined);
  const displayImage = recommendation?.coverImage || imageUrl || null;

  const perTraveler = recommendation?.perTravelerCosts;
  const breakdown = perTraveler?.breakdown || recommendation?.costBreakdown;

  const hasBreakdown =
    !!breakdown &&
    (breakdown.transportation !== undefined ||
      breakdown.accommodation !== undefined ||
      breakdown.activities !== undefined ||
      breakdown.food !== undefined ||
      breakdown.other !== undefined);

  if (!recommendation) return null;

  return (
    <Modal
      visible={visible}
      transparent={false}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        <ScrollView showsVerticalScrollIndicator={false}>
          {/* Hero */}
          <View style={{ position: 'relative' }}>
            <View style={{ width: '100%', height: 220, backgroundColor: theme.colors.borderMuted }}>
              {displayImage ? (
                <Image source={{ uri: displayImage }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
              ) : (
                <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                  <Ionicons name="image-outline" size={64} color={theme.colors.textSecondary} />
                </View>
              )}
            </View>

            <Pressable
              onPress={onClose}
              style={{
                position: 'absolute',
                top: theme.spacing(2),
                right: theme.spacing(2),
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radii.pill,
                padding: theme.spacing(1),
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                ...theme.shadows.level2,
              }}
            >
              <Ionicons name="close" size={22} color={theme.colors.text} />
            </Pressable>
          </View>

          <View style={{ padding: theme.spacing(2), gap: theme.spacing(2) }}>
            <View style={{ gap: theme.spacing(0.75) }}>
              <ThemedText variant="h2" style={{ fontSize: 24 }}>
                {titleText}
              </ThemedText>

              {recommendation.title ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
                  <Body color={theme.colors.textSecondary}>{destination}</Body>
                </View>
              ) : null}

              {recommendation.dates ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
                  <Body color={theme.colors.textSecondary}>{recommendation.dates}</Body>
                </View>
              ) : null}
            </View>

            {recommendation.description ? <Body style={{ lineHeight: 22 }}>{recommendation.description}</Body> : null}

            {(perTraveler?.total || recommendation.estimatedBudget || hasBreakdown) && (
              <Card
                variant="outlined"
                style={{
                  backgroundColor: theme.colors.borderMuted,
                  borderColor: theme.colors.borderMuted,
                }}
              >
                <View style={{ gap: theme.spacing(1) }}>
                  <SubHeading>Cost Breakdown</SubHeading>

                  {recommendation.numberOfTravelers && recommendation.numberOfTravelers > 1 ? (
                    <Caption color={theme.colors.textSecondary}>
                      For {recommendation.numberOfTravelers} travelers
                    </Caption>
                  ) : null}

                  {perTraveler?.total ? (
                    <Body style={{ fontWeight: '700' }}>
                      Total trip cost: ${perTraveler.total.toLocaleString()}/person
                    </Body>
                  ) : typeof recommendation.estimatedBudget === 'number' ? (
                    <Body style={{ fontWeight: '700' }}>
                      Total trip cost: ${recommendation.estimatedBudget.toLocaleString()}
                      {recommendation.numberOfTravelers && recommendation.numberOfTravelers > 1 ? '/group' : ''}
                    </Body>
                  ) : null}

                  {hasBreakdown ? (
                    <View style={{ gap: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}>
                      {typeof breakdown?.transportation === 'number' && breakdown.transportation > 0 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Caption color={theme.colors.textSecondary}>Flight:</Caption>
                          <Caption color={theme.colors.text}>${breakdown.transportation.toLocaleString()}</Caption>
                        </View>
                      )}
                      {typeof breakdown?.accommodation === 'number' && breakdown.accommodation > 0 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Caption color={theme.colors.textSecondary}>
                            Accommodation{recommendation.duration ? ` (${recommendation.duration} days)` : ''}:
                          </Caption>
                          <Caption color={theme.colors.text}>${breakdown.accommodation.toLocaleString()}</Caption>
                        </View>
                      )}
                      {typeof breakdown?.activities === 'number' && breakdown.activities > 0 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Caption color={theme.colors.textSecondary}>Activities:</Caption>
                          <Caption color={theme.colors.text}>${breakdown.activities.toLocaleString()}</Caption>
                        </View>
                      )}
                      {typeof breakdown?.food === 'number' && breakdown.food > 0 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Caption color={theme.colors.textSecondary}>Food:</Caption>
                          <Caption color={theme.colors.text}>${breakdown.food.toLocaleString()}</Caption>
                        </View>
                      )}
                      {typeof breakdown?.other === 'number' && breakdown.other > 0 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Caption color={theme.colors.textSecondary}>Other:</Caption>
                          <Caption color={theme.colors.text}>${breakdown.other.toLocaleString()}</Caption>
                        </View>
                      )}
                    </View>
                  ) : null}
                </View>
              </Card>
            )}

            {recommendation.whyThisMatch ? (
              <Card
                variant="outlined"
                style={{
                  backgroundColor: theme.colors.borderMuted,
                  borderColor: theme.colors.borderMuted,
                }}
              >
                <View style={{ gap: theme.spacing(0.5) }}>
                  <SubHeading>Why this matches</SubHeading>
                  <Caption color={theme.colors.textSecondary}>{recommendation.whyThisMatch}</Caption>
                </View>
              </Card>
            ) : null}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}


